import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Droplets, Leaf, ArrowRight, CheckCircle, Beaker, AlertTriangle, Lightbulb, Target, Zap, Shield, Award } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropGrid from '../components/CropGrid';
import UnifiedContactModal from '../components/UnifiedContactModal';

const SulfurBurnerGuidePage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Better Water, Better Profits: A Holistic Guide to pH Management"
        description="A comprehensive guide to pH management, soil health, and sustainable practices. Learn how to feed your soil and love your crops through proper water quality management."
        canonicalPath="/sulfur-burner-guide"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[80vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background with Water Animation */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.pexels.com/photos/1595104/pexels-photo-1595104.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Modern irrigation system with water spraying over green agricultural fields"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-cyan-800/70 to-black/60"></div>
          
          {/* Animated Water Droplets */}
          <div className="absolute inset-0">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute water-droplet animate-pulse"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              >
                <Droplets className="text-white/30" size={12} />
              </div>
            ))}
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
          >
            <Droplets size={16} />
            <span>Comprehensive Agricultural Guide</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight"
          >
            Better Water, Better Profits:
            <span className="block text-cyan-300">A Holistic Guide to pH Management</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto"
          >
            Feed Your Soil - Love Your Crops
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            onClick={() => setIsContactModalOpen(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-cyan-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-cyan-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
          >
            <span>Get Your Water Analysis</span>
            <ArrowRight size={20} className="md:w-6 md:h-6" />
          </motion.button>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-cyan-100 text-cyan-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Lightbulb size={16} />
              <span>Introduction</span>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-cyan-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
                Water quality is an often overlooked yet critical factor in sustainable agriculture. For agronomists, sustainability consultants, commercial growers, and irrigation managers, managing irrigation water pH and alkalinity is key to unlocking soil fertility and crop performance.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                In many arid and irrigated regions, groundwater and surface water tend to be highly alkaline (high pH) due to dissolved carbonates and bicarbonates. Left unaddressed, high-pH irrigation water can lead to nutrient deficiencies, soil structure problems, and reduced efficacy of sustainable farming practices.
              </p>
            </div>
          </motion.div>

          {/* Choose Your Path */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
              Choose Your Path
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              Whether you need a quick sizing quote or comprehensive chemistry analysis, we've got you covered.
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <Link to="/instant-quote">
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-2 border-cyan-200 hover:border-cyan-400 transition-all h-full cursor-pointer"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                      <Zap className="text-white" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Need a quote fast?</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Get an instant size and quote in 60 seconds. No lab report needed—just pH and peak GPM.
                  </p>
                  <div className="flex items-center text-cyan-600 font-semibold">
                    <span>Get Instant Quote</span>
                    <ArrowRight size={20} className="ml-2" />
                  </div>
                </motion.div>
              </Link>

              <Link to="/chemistry-review">
                <motion.div
                  whileHover={{ scale: 1.02, y: -4 }}
                  className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-200 hover:border-blue-400 transition-all h-full cursor-pointer"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Beaker className="text-white" size={24} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Want a chemistry sanity-check action plan?</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Get a free chemistry review and action plan. Uploads optional—we'll start with pH + GPM and tell you what to test next.
                  </p>
                  <div className="flex items-center text-blue-600 font-semibold">
                    <span>Get Chemistry Review</span>
                    <ArrowRight size={20} className="ml-2" />
                  </div>
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Water pH Challenges */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Water pH Challenges in Arid and Irrigated Regions
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                In regions where evaporation exceeds precipitation – for example, much of the U.S. West and other semi-arid farming zones – alkaline irrigation water is a common hurdle. Water in these areas often contains high levels of bicarbonate (HCO₃⁻) from geological limestone (calcite) sources, which drives the pH upward.
              </p>
              
              <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Bicarbonate (HCO₃⁻) Interpretation Tiers</h3>
                <p className="text-gray-700 mb-4">
                  If your irrigation water pH is above about 7.4, it's likely loaded with bicarbonates. Rather than a single threshold, bicarbonate impact follows a tiered pattern:
                </p>
                <div className="space-y-3 mb-4">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-semibold text-green-800 mb-1">&lt; 120 ppm</p>
                    <p className="text-gray-700 text-sm">Typically manageable; monitor and confirm target pH stability at point of use.</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="font-semibold text-yellow-800 mb-1">120–150 ppm</p>
                    <p className="text-gray-700 text-sm">Commonly where downstream issues begin depending on hardness, target pH, and injection/turbulence quality.</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="font-semibold text-red-800 mb-1">&gt; 150 ppm</p>
                    <p className="text-gray-700 text-sm">Usually merits a mitigation plan; confirm with full water report and operating constraints.</p>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-700 text-sm">
                    <strong>Important:</strong> We can size and quote off pH + peak GPM. A full water report helps tighten expectations and commissioning targets.
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Problems Caused by High Bicarbonates:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">Scale and Clogging</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Lime scale builds up in pipes and on foliage</li>
                    <li>• Plugged irrigation equipment</li>
                    <li>• Uneven water distribution</li>
                    <li>• Wasted water and energy</li>
                  </ul>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">Soil Structural Decline</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Calcium and magnesium precipitation</li>
                    <li>• Soil aggregate breakdown</li>
                    <li>• Surface crusting and compaction</li>
                    <li>• Reduced water infiltration</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Nutrient Availability */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Leaf className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Nutrient Availability and Plant Health: Why pH Matters
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Soil pH fundamentally governs which nutrients are available to plants. Most crops thrive in a slightly acidic to neutral soil pH (roughly 6.0 to 7.0) because that is the sweet spot where essential nutrients stay soluble and accessible."
              </blockquote>
              
              <p className="text-gray-700 mb-6">
                A 2019 study from the University of Florida found that high soil pH reduced crop uptake of N, P, K, Ca, Mg, Mn, and Zn by reducing their solubility in soil water. When pH is high, a plant can literally be starving for nutrients in the midst of plenty.
              </p>

              <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Iron Chlorosis: A Common Symptom</h3>
                <p className="text-gray-700 mb-4">
                  One common symptom of high-pH, high-bicarbonate conditions is iron chlorosis – yellowing of young leaves due to iron deficiency – even when total iron in the soil is high. This happens because bicarbonate elevates pH and interferes with iron metabolism in the plant.
                </p>
                <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium">
                    ✅ Research in citrus groves showed that adjusting irrigation water pH into the mildly acidic range boosted leaf nutrient levels and yield per tree as soil pH dropped to about 6.0.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">High pH Problems (&gt;7.5)</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Iron, zinc, manganese deficiencies</li>
                    <li>• Reduced phosphorus availability</li>
                    <li>• Poor nitrogen and potassium uptake</li>
                    <li>• Hidden hunger symptoms</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">Optimal pH Benefits (6.0-7.0)</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Maximum nutrient availability</li>
                    <li>• Improved fertilizer efficiency</li>
                    <li>• Better crop performance</li>
                    <li>• Reduced input costs</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Soil Structure and Biology */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Beaker className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                The Soil Biology Connection: pH, Microbes, and Nutrient Cycling
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                A healthy soil isn't just minerals and nutrients – it's a living ecosystem teeming with microbes that drive nutrient cycling, build soil structure, and protect plants. Soil biology and pH are deeply interconnected.
              </p>
              
              <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Research Findings</h3>
                <p className="text-gray-700 mb-4">
                  A large-scale study in China found soil pH to be more influential than soil nutrient levels in shaping which bacteria were present and how diverse the community was. Extreme pH conditions tend to reduce microbial diversity and can suppress beneficial microbes that crops rely on.
                </p>
                <blockquote className="text-gray-700 italic border-l-4 border-blue-500 pl-4">
                  "Beneficial soil microbes play a critical role in the acquisition and transfer of soil nutrients into plants." - Natallia Gulbis, Soil Microbiology Expert
                </blockquote>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Microbial Functions:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Decomposing organic matter',
                  'Fixing nitrogen naturally',
                  'Solubilizing minerals for plants',
                  'Suppressing soil pathogens',
                  'Forming symbiotic root relationships',
                  'Building soil structure through exudates'
                ].map((function_, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white rounded-lg p-3">
                    <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                    <span className="text-gray-700 text-sm">{function_}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Regenerative Strategies */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <Target className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Regenerative Strategies for Managing Alkaline Water and Soil
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Achieving sustainable water quality in agriculture isn't just about one tool or one input – it's about an integrated approach that combines pH management, soil amendments, and good farming practices.
              </p>

              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Test and Monitor</h3>
                  <p className="text-gray-700 mb-4">
                    You can't manage what you don't measure. Water analysis should include pH, alkalinity (bicarbonate/carbonate levels), electrical conductivity (salinity), and SAR (sodium hazard).
                  </p>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-purple-800 font-medium">
                      🔬 Bicarbonate &gt; 150 ppm typically merits a mitigation plan; see tiered guidance for full context
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Apply Organic Matter and Soil Amendments</h3>
                  <p className="text-gray-700 mb-4">
                    Building soil organic matter helps buffer pH imbalances. Incorporating composts, cover crop residues, and other organic materials releases organic acids that can buffer soil pH and chelate nutrients.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Gypsum (calcium sulfate) for sodium displacement</li>
                    <li>• Elemental sulfur for gradual pH reduction</li>
                    <li>• Compost and organic matter for buffering</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">3. Integrate pH Management into Fertigation</h3>
                  <p className="text-gray-700 mb-4">
                    Some fertilizers can help neutralize alkalinity while providing nutrients. Ammonium-based fertilizers have an acidifying effect on soil over time as ammonium converts to nitrate.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Solutions for Correcting Alkaline Water */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Zap className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Solutions for Correcting Alkaline Irrigation Water
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 md:p-8 border border-cyan-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                The most direct way to counteract high-pH, high-alkalinity water is to neutralize the excess bicarbonate with an acid. When acid is added to water, it supplies hydrogen ions (H⁺) that react with bicarbonate (HCO₃⁻) to form carbon dioxide and water.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Mineral Acids</h3>
                  <p className="text-gray-700 mb-3 text-sm">Sulfuric, nitric, and phosphoric acids are very effective but extremely hazardous to handle.</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Highly effective</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="text-red-500" size={14} />
                      <span className="text-gray-600 text-sm">Safety concerns</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="text-red-500" size={14} />
                      <span className="text-gray-600 text-sm">Environmental risks</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Organic Acids</h3>
                  <p className="text-gray-700 mb-3 text-sm">Citric and acetic acids are safer but require higher volumes and costs.</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Safe to handle</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Commonly compatible—confirm with certifier</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="text-orange-500" size={14} />
                      <span className="text-gray-600 text-sm">Higher cost</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Carbon Dioxide Injection</h3>
                  <p className="text-gray-700 mb-3 text-sm">CO₂ creates carbonic acid in water - safe but requires specialized equipment.</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Very safe</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">No unwanted ions</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="text-orange-500" size={14} />
                      <span className="text-gray-600 text-sm">Expensive equipment</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 shadow-md border-2 border-green-300">
                  <h3 className="text-lg font-semibold text-green-800 mb-4">⭐ Sulfur Burners (Recommended)</h3>
                  <p className="text-gray-700 mb-3 text-sm">Burns elemental sulfur to create sulfurous acid - effective, safe, and sustainable.</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Highly effective</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Commonly compatible—confirm with certifier</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Adds sulfur nutrition</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500" size={14} />
                      <span className="text-gray-600 text-sm">Cost effective</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Sulfur Burners: A Sustainable Solution */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Shield className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Sulfur Burners: A Sustainable Solution for Alkaline Water
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 md:p-8 border border-green-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Integrating a sulfur burner into an irrigation system can be a game-changer for farms facing alkalinity issues. Unlike a one-time chemical addition, a sulfur burner provides an ongoing, controlled means of treating water.
              </p>

              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Effective Bicarbonate Neutralization</h3>
                  <p className="text-gray-700 mb-4">
                    Sulfurous acid produced by these burners reacts readily with bicarbonates in water and soil. University research in Utah noted that sulfur burners have the potential to improve irrigation water quality, reclaim sodic soils, and improve soil infiltration and drainage in calcareous soil regions.
                  </p>
                  <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium">
                      📊 West Texas cotton study: 20% increase in lint yield with 19% reduction in soil salinity after one year of sulfur burner use
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Synergy with Soil Biology</h3>
                  <p className="text-gray-700 mb-4">
                    Sulfur is an essential plant nutrient and also feeds certain beneficial soil microbes. By maintaining a more neutral pH in the root zone, sulfur burner use creates a more hospitable environment for beneficial bacteria and mycorrhizal fungi to flourish.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Enhanced response to biofertilizers</li>
                    <li>• Improved microbial inoculant effectiveness</li>
                    <li>• Better nutrient cycling</li>
                    <li>• Reduced fertilizer and pesticide needs</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Commonly Compatible with Many Programs</h3>
                  <p className="text-gray-700 mb-4">
                    Elemental sulfur is mined or recovered from natural sources and commonly compatible with many programs—confirm with your certifier and inputs list. There are no petrochemical or industrial by-products being added – just sulfur, which the system converts to a mild acid.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      'Commonly compatible—verify with certifier',
                      'No toxic residues left behind',
                      'Protects downstream water quality',
                      'Works with natural soil chemistry'
                    ].map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Texas A&M Research Callout - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 rounded-2xl p-6 md:p-8 border-2 border-blue-300 shadow-lg">
              <div className="flex items-start space-x-3 mb-4">
                <Beaker className="text-blue-600 flex-shrink-0 mt-1" size={28} />
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Texas A&M AgriLife (Far West Texas Cotton): What They Reported
                </h3>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">One-season reported reduction in upper root-zone salinity</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">Modest SAR changes varying by depth (distribution matters)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">~20% cotton lint yield improvement relative to long-term average, plus fiber quality improvements</span>
                </li>
              </ul>

              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 mb-4 border border-blue-200">
                <p className="text-gray-700 mb-1 leading-relaxed">
                  One season is informative, not conclusive—multi-year validation is still needed.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/blog/texas-a-m-agrilife-cotton-study-sulfur-burner-treated-irrigation-reduced-root-zone-salinity-and-increased-yield-20"
                  className="block"
                >
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-5 py-3 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-md">
                    <p className="font-bold mb-1">
                      Read our breakdown
                    </p>
                    <p className="text-sm text-blue-100 flex items-center">
                      Full article <ArrowRight size={14} className="ml-2" />
                    </p>
                  </div>
                </Link>
                <a
                  href="https://agrilifetoday.tamu.edu/2024/07/10/sulfur-burner-helps-improve-cotton-production-in-far-west-texas/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <div className="border-2 border-blue-600 text-blue-600 px-5 py-3 rounded-lg hover:bg-blue-600 hover:text-white transition-all duration-300">
                    <p className="font-bold mb-1">
                      Texas A&M source
                    </p>
                    <p className="text-sm flex items-center">
                      External link <ArrowRight size={14} className="ml-2" />
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </motion.div>

          {/* OSU Research Spotlight Callout */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 rounded-2xl p-6 md:p-8 border-2 border-green-300 shadow-lg">
              <div className="flex items-start space-x-3 mb-4">
                <Beaker className="text-green-600 flex-shrink-0 mt-1" size={28} />
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
                  Research Spotlight: Organic Blueberries in Hermiston, Oregon
                </h3>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Sulfur burners are not just a theory. In an organic northern highbush blueberry trial in Hermiston, Oregon, researchers at Oregon State University:
              </p>

              <ul className="space-y-2 mb-6 ml-4">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">Established blueberries on sandy, calcareous soil with high-pH irrigation water</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">Pre-acidified the soil with elemental sulfur before planting</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">Irrigated through drip equipped with a sulfur burner and sand media filtration</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={16} />
                  <span className="text-gray-700">Used acidified irrigation as the backbone pH tool while they fine-tuned compost and soil health strategies around it</span>
                </li>
              </ul>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Their goal was to keep soil pH in the blueberry sweet spot while dealing with the same high-pH, high-bicarbonate conditions many commercial growers face.
              </p>

              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-5 mb-4 border border-blue-200">
                <p className="text-gray-700 mb-3 leading-relaxed">
                  We break down what this work means for real farms, in plain English, in our article:
                </p>
                <Link
                  to="/blog/blueberries-high-ph-water-and-osu-s-organic-trial-what-acidified-irrigation-really-buys-you"
                  className="block"
                >
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-5 py-3 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 shadow-md">
                    <p className="font-bold mb-1">
                      Blueberries, High-pH Water, and OSU's Organic Trial in Hermiston: What Acidified Irrigation Really Buys You
                    </p>
                    <p className="text-sm text-blue-100 flex items-center">
                      Read the full article <ArrowRight size={14} className="ml-2" />
                    </p>
                  </div>
                </Link>
              </div>

              <div className="text-xs text-gray-600 italic leading-relaxed border-t border-blue-200 pt-4">
                <p>
                  <em>Sources: Organic blueberry soil health research from Oregon State University in Hermiston, Oregon (Scientific Reports, OSU Extension, and eOrganic outreach). These resources are provided for context; CTC Waterworks is not speaking on behalf of Oregon State University.</em>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Mechanism Section - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                <Beaker className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Mechanism: Why Acidified Irrigation Can Matter in Calcareous Soils
              </h2>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-6 md:p-8 border border-gray-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Acidification lowers water pH and shifts carbonate equilibrium, which can influence how calcium behaves in calcareous (limestone-rich) soil contexts. This is not universal—it's chemistry that matters when soil type, drainage reality, irrigation scheduling, and field constraints align.
              </p>

              <ul className="space-y-4 mb-6">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-gray-700"><strong>Improves availability/mobility of calcium</strong> in calcareous contexts where relevant, supporting better soil structure when other conditions are met.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-gray-700"><strong>Supports infiltration/permeability dynamics</strong> when sodicity is part of the issue and displacement of sodium is feasible.</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-gray-700"><strong>Enables leaching of salts</strong> only when leaching and drainage are realistic—acidified water does not create drainage where none exists.</p>
                  </div>
                </li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mb-4">
                <p className="text-gray-800 font-medium">
                  This is not universal; soil type, drainage, irrigation scheduling, and constraints determine outcomes.
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                <p className="text-gray-700 text-sm">
                  In the right conditions, treated irrigation can support soil health goals by improving root-zone chemistry stability and enabling more effective salt management where leaching is realistic.
                </p>
              </div>
            </div>
          </motion.div>

          {/* What to Send Us CTA - NEW */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 border-2 border-cyan-300 shadow-lg">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Evaluate Fit?</h2>
                <p className="text-lg text-gray-700">
                  Send your water + soil profile and we'll tell you if treated irrigation is worth pursuing.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Minimum to start (keeps it simple):</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                    <span className="text-gray-700">Flow rate (GPM)</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                    <span className="text-gray-700">Current pH</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-cyan-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                    <span className="text-gray-700">Target pH</span>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-4">If you have it, we can deliver a more personalized plan and tighter expectations:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700 text-sm"><strong>Alkalinity/bicarbonate:</strong> Helps estimate demand and reduce surprises</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700 text-sm"><strong>Hardness (Ca/Mg):</strong> Helps assess scaling tendency and maintenance risk</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700 text-sm"><strong>Sodium/EC:</strong> Helps determine whether salinity/sodicity outcomes are relevant</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700 text-sm"><strong>Soil profile by depth:</strong> Only if your goal involves root-zone salinity/sodicity/infiltration outcomes</span>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mt-4 italic">
                  Not everyone needs every test. More inputs simply mean fewer assumptions—and a tighter plan.
                </p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center space-x-2"
                >
                  <span>Get Water Analysis</span>
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>
          </motion.div>

          {/* When and Where to Use */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Award className="text-white" size={24} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                When and Where to Use Sulfur Burners
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 md:p-8 border border-yellow-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Not every farm will need a sulfur burner, but certain conditions and crop situations make them a highly worthwhile investment.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Arid Regions with High Bicarbonate Water</h3>
                  <p className="text-gray-700 mb-3 text-sm">Classic case for sulfur burners - high alkalinity and pH above neutral.</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    <li>• California's Central Valley</li>
                    <li>• West Texas</li>
                    <li>• Arizona and Colorado</li>
                    <li>• Australia's Murray-Darling Basin</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Calcareous Soils and Perennial Crops</h3>
                  <p className="text-gray-700 mb-3 text-sm">Critical for long-term tree and vine health.</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    <li>• Citrus orchards</li>
                    <li>• Vineyards</li>
                    <li>• Almond and stone fruits</li>
                    <li>• Avocado groves</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Field Crops with Salt/Sodicity Issues</h3>
                  <p className="text-gray-700 mb-3 text-sm">Large-scale applications for soil reclamation.</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    <li>• Cotton fields</li>
                    <li>• Alfalfa production</li>
                    <li>• Corn and small grains</li>
                    <li>• Sodic soil reclamation</li>
                  </ul>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">High-Value Vegetables and Berries</h3>
                  <p className="text-gray-700 mb-3 text-sm">Crops with low tolerance for alkaline conditions.</p>
                  <ul className="space-y-1 text-gray-600 text-sm">
                    <li>• Blueberries (require pH 5.0-5.5)</li>
                    <li>• Strawberries and raspberries</li>
                    <li>• Greenhouse vegetables</li>
                    <li>• Hydroponic systems</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Crop-Specific Guides Section */}
      <CropGrid 
        title="Crop-Specific pH Guides"
        subtitle="Click any crop to learn how sulfur burners can optimize your soil pH and boost yields"
      />

      {/* Conclusion */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-cyan-900 via-blue-800 to-black text-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Conclusion: The Path to Better Water, Better Profits
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90 leading-relaxed">
              Managing water quality – especially pH and alkalinity – is an essential component of modern sustainable agriculture. By integrating pH management with soil-centered practices, growers can turn a liability (alkaline water) into an asset.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8">
              <h3 className="text-xl font-bold text-white mb-4">Key Takeaways:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {[
                  'Sulfur burners provide effective, sustainable pH management',
                  'Improved nutrient uptake and soil structure',
                  'Commonly compatible with many programs—confirm with certifier',
                  'Many growers report yield improvements when pH risks are controlled',
                  'Long-term soil health and productivity benefits',
                  'Cost-effective solution for large-scale operations'
                ].map((takeaway, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                    <span className="text-white/90 text-sm">{takeaway}</span>
                  </div>
                ))}
              </div>
            </div>

            <motion.button
              onClick={() => setIsContactModalOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-cyan-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-cyan-600 transition-colors inline-flex items-center space-x-3 shadow-lg"
            >
              <span>Start Your Water Quality Journey</span>
              <ArrowRight size={20} />
            </motion.button>

            <div className="mt-8 text-sm opacity-80">
              <p>Trusted by 1000+ growers nationwide • 40+ years of proven results</p>
            </div>
            

          </motion.div>
        </div>
      </section>

      {/* Contact Modal */}
      <UnifiedContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        productType="sulfur-burners"
        formSource="guide_download"
        title="Get Your Free Water Analysis"
        subtitle="Let our experts analyze your water and recommend the best pH management solution"
      />

      <Footer />
    </div>
  );
};

export default SulfurBurnerGuidePage;